import React, { useState } from 'react';
import { 
  AppBar, 
  Toolbar, 
  Button, 
  Box,
  Container,
  IconButton,
  Menu,
  MenuItem,
  Avatar,
  Divider,
  ListItemIcon,
  Fade
} from '@mui/material';
import { 
  Menu as MenuIcon,
  Dashboard,
  SportsEsports,
  GroupAdd,
  Logout,
  Person
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { motion } from 'framer-motion';
import logo from '../../assets/logo.png';

const MotionBox = motion(Box);

function Navbar() {
  const { currentUser, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileMenu, setMobileMenu] = useState(null);
  const [userMenu, setUserMenu] = useState(null);

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  return (
    <AppBar 
      position="sticky" 
      sx={{ 
        backgroundColor: 'white',
        boxShadow: '0 2px 10px rgba(0,0,0,0.1)'
      }}
    >
      <Container>
        <Toolbar sx={{ justifyContent: 'space-between', py: 1 }}>
          {/* Logo */}
          <MotionBox
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
            sx={{ cursor: 'pointer' }}
            onClick={() => navigate('/dashboard')}
          >
            <img 
              src={logo} 
              alt="AirUpEnglish Logo" 
              style={{ 
                height: 40,
                width: 'auto'
              }} 
            />
          </MotionBox>

          {/* Desktop Menu */}
          <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center' }}>
            {currentUser ? (
              <>
                <Button
                  color="primary"
                  startIcon={<Dashboard />}
                  onClick={() => navigate('/dashboard')}
                  sx={{ 
                    mx: 1,
                    '&:hover': {
                      backgroundColor: 'rgba(0, 191, 13, 0.1)',
                    }
                  }}
                >
                  Ana Sayfa
                </Button>
                <Button
                  color="primary"
                  startIcon={<SportsEsports />}
                  onClick={() => navigate('/play/single')}
                  sx={{ 
                    mx: 1,
                    '&:hover': {
                      backgroundColor: 'rgba(0, 191, 13, 0.1)',
                    }
                  }}
                >
                  Tek Oyunculu
                </Button>
                <Button
                  color="primary"
                  startIcon={<GroupAdd />}
                  onClick={() => navigate('/play/duo')}
                  sx={{ 
                    mx: 1,
                    '&:hover': {
                      backgroundColor: 'rgba(0, 191, 13, 0.1)',
                    }
                  }}
                >
                  İkili Mod
                </Button>
                <IconButton
                  onClick={(e) => setUserMenu(e.currentTarget)}
                  sx={{ ml: 2 }}
                >
                  <Avatar 
                    sx={{ 
                      bgcolor: 'primary.main',
                      width: 35,
                      height: 35,
                      fontSize: '1rem'
                    }}
                  >
                    {currentUser?.username?.charAt(0).toUpperCase()}
                  </Avatar>
                </IconButton>
                <Menu
                  anchorEl={userMenu}
                  open={Boolean(userMenu)}
                  onClose={() => setUserMenu(null)}
                  TransitionComponent={Fade}
                  PaperProps={{
                    sx: {
                      mt: 1,
                      minWidth: 180,
                      boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
                    }
                  }}
                >
                  <MenuItem sx={{ color: 'text.secondary' }}>
                    <ListItemIcon>
                      <Person fontSize="small" />
                    </ListItemIcon>
                    {currentUser?.username}
                  </MenuItem>
                  <Divider />
                  <MenuItem 
                    onClick={handleLogout}
                    sx={{ color: 'error.main' }}
                  >
                    <ListItemIcon>
                      <Logout fontSize="small" color="error" />
                    </ListItemIcon>
                    Çıkış Yap
                  </MenuItem>
                </Menu>
              </>
            ) : (
              <>
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={() => navigate('/login')}
                  sx={{ 
                    mx: 1,
                    borderColor: '#00bf0d',
                    color: '#00bf0d',
                    '&:hover': {
                      borderColor: '#00bf0d',
                      backgroundColor: 'rgba(0, 191, 13, 0.1)',
                    }
                  }}
                >
                  Giriş Yap
                </Button>
                <Button
                  variant="contained"
                  onClick={() => navigate('/register')}
                  sx={{
                    mx: 1,
                    background: 'linear-gradient(45deg, #00bf0d 30%, #4cf13d 90%)',
                    boxShadow: '0 3px 5px 2px rgba(0, 191, 13, .3)',
                    '&:hover': {
                      background: 'linear-gradient(45deg, #00bf0d 60%, #4cf13d 90%)',
                      boxShadow: '0 4px 10px 2px rgba(0, 191, 13, .4)',
                    }
                  }}
                >
                  Kayıt Ol
                </Button>
              </>
            )}
          </Box>

          {/* Mobile Menu */}
          <Box sx={{ display: { xs: 'flex', md: 'none' } }}>
            <IconButton
              size="large"
              onClick={(e) => setMobileMenu(e.currentTarget)}
              color="primary"
            >
              <MenuIcon />
            </IconButton>
            <Menu
              anchorEl={mobileMenu}
              open={Boolean(mobileMenu)}
              onClose={() => setMobileMenu(null)}
              TransitionComponent={Fade}
              PaperProps={{
                sx: {
                  mt: 1,
                  minWidth: 200,
                  boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
                }
              }}
            >
              {currentUser ? (
                <>
                  <MenuItem 
                    onClick={() => {
                      navigate('/dashboard');
                      setMobileMenu(null);
                    }}
                  >
                    <ListItemIcon>
                      <Dashboard fontSize="small" />
                    </ListItemIcon>
                    Ana Sayfa
                  </MenuItem>
                  <MenuItem 
                    onClick={() => {
                      navigate('/play/single');
                      setMobileMenu(null);
                    }}
                  >
                    <ListItemIcon>
                      <SportsEsports fontSize="small" />
                    </ListItemIcon>
                    Tek Oyunculu
                  </MenuItem>
                  <MenuItem 
                    onClick={() => {
                      navigate('/play/duo');
                      setMobileMenu(null);
                    }}
                  >
                    <ListItemIcon>
                      <GroupAdd fontSize="small" />
                    </ListItemIcon>
                    İkili Mod
                  </MenuItem>
                  <Divider />
                  <MenuItem 
                    onClick={handleLogout}
                    sx={{ color: 'error.main' }}
                  >
                    <ListItemIcon>
                      <Logout fontSize="small" color="error" />
                    </ListItemIcon>
                    Çıkış Yap
                  </MenuItem>
                </>
              ) : (
                <>
                  <MenuItem 
                    onClick={() => {
                      navigate('/login');
                      setMobileMenu(null);
                    }}
                  >
                    Giriş Yap
                  </MenuItem>
                  <MenuItem 
                    onClick={() => {
                      navigate('/register');
                      setMobileMenu(null);
                    }}
                  >
                    Kayıt Ol
                  </MenuItem>
                </>
              )}
            </Menu>
          </Box>
        </Toolbar>
      </Container>
    </AppBar>
  );
}

export default Navbar;
